import { useState } from 'react';
import {
  AssignmentEntityType,
  CREATE_ASSIGNMENT,
  FE_DELETE_ASSIGNMENTS,
  useAssignmentUrl,
} from 'domain/shared/assignment';
import { useAuthorization } from 'framework/authorization';
import { AddressableViewLayout } from 'framework/ViewTypes';
import { bindCollection } from 'framework/ViewTypes/contentLayout';
import { useAsyncValue } from 'framework/utils';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { getAssignmentsTableConfig } from './assignmentsTableConfig';
import DeleteAssignmentDialog from './DeleteAssignmentDialog';
import useAssignmentsCollection from './useAssignmentsCollection';
import useAssignmentsFilterBar from './useAssignmentsFilterBar';
import AssignmentFormDrawer from '../createupdate/AssignmentFormDrawer';
import { AssignmentDrawerMode } from '../createupdate/types';

type AssignmentDrawerState = { mode: AssignmentDrawerMode; assignmentId?: string };

const AssignmentList = () => {
  const [drawer, setDrawer] = useState<AssignmentDrawerState>();
  const { isGranted } = useAuthorization();
  const { getUserAlias } = useAuthenticatedUserService();
  const userAlias = useAsyncValue(getUserAlias);
  const { getSingleEntityUrl } = useAssignmentUrl();
  const [assignmentToDelete, setAssignmentToDelete] = useState<AssignmentEntityType | null>(null);
  const collection = useAssignmentsCollection();
  const filterProps = useAssignmentsFilterBar(collection);

  const tableConfig = getAssignmentsTableConfig(collection.rows, getSingleEntityUrl, {
    canCreate: isGranted(CREATE_ASSIGNMENT),
    canDelete: isGranted(FE_DELETE_ASSIGNMENTS),
    userAlias,
    onCreate: () => setDrawer({ mode: AssignmentDrawerMode.CREATE }),
    onCopy: row => setDrawer({ mode: AssignmentDrawerMode.COPY, assignmentId: String(row.id) }),
    onEdit: row => setDrawer({ mode: AssignmentDrawerMode.EDIT, assignmentId: String(row.id) }),
    onDelete: setAssignmentToDelete,
  });

  const formDrawer = drawer && (
    <AssignmentFormDrawer
      key={`${drawer.mode}-${drawer.assignmentId ?? ''}`}
      mode={drawer.mode}
      assignmentId={drawer.assignmentId}
      onClose={() => setDrawer(undefined)}
      onSuccess={collection.reload}
    />
  );

  const binding = bindCollection(collection, tableConfig, { filterProps });

  return (
    <>
      <AddressableViewLayout title="Assignments" drawer={formDrawer} {...binding.pageProps} />
      {assignmentToDelete && (
        <DeleteAssignmentDialog
          assignment={assignmentToDelete}
          onClose={() => setAssignmentToDelete(null)}
          onDeleted={collection.reload}
        />
      )}
    </>
  );
};

export default AssignmentList;
